import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { alpha, useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import type { ReactElement, ReactNode } from 'react'
import { forwardRef } from 'react'
import type { NavLinkProps as RouterNavLinkProps } from 'react-router-dom'
import { NavLink as RouterNavLink, useLocation, useMatch } from 'react-router-dom'

import { SIDEBAR_TRANSITION_DURATION_MS, SIDEBAR_TRANSITION_EASING } from '@/constants/layout'
import environment from '@/environment'

const Link = forwardRef<HTMLAnchorElement, RouterNavLinkProps>(function Link(itemProps, ref) {
  return <RouterNavLink ref={ref} {...itemProps} role={undefined} />
})

function getMargin(envTheme, theme) {
  if (envTheme === 'sympower') return theme.spacing(0)
}

const ITEM_COLOR_TRANSITION =
  'background-color 160ms cubic-bezier(0.16, 1, 0.3, 1), color 160ms cubic-bezier(0.16, 1, 0.3, 1)'

// Label fades out fast on collapse so it's gone before the rail crops it.
// On expand, fade in is delayed by ~half the sidebar duration so it appears once there's room.
const LABEL_FADE_OUT_MS = 120
const LABEL_FADE_IN_MS = 200
const LABEL_FADE_IN_DELAY_MS = Math.round(SIDEBAR_TRANSITION_DURATION_MS * 0.55)

function labelTransition(isCollapsed: boolean) {
  return isCollapsed
    ? `opacity ${LABEL_FADE_OUT_MS}ms ${SIDEBAR_TRANSITION_EASING}, transform ${LABEL_FADE_OUT_MS}ms ${SIDEBAR_TRANSITION_EASING}`
    : `opacity ${LABEL_FADE_IN_MS}ms ${SIDEBAR_TRANSITION_EASING} ${LABEL_FADE_IN_DELAY_MS}ms, transform ${LABEL_FADE_IN_MS}ms ${SIDEBAR_TRANSITION_EASING} ${LABEL_FADE_IN_DELAY_MS}ms`
}

type ListItemLinkProps = {
  /** Optional leading icon. Omit on nav sub-items where we want a leaner, text-only treatment. */
  icon?: ReactElement
  activePath: string | ((pathname: string) => boolean)
  isCollapsed?: boolean
  primary: string
  to: string
  onClick?: () => void
}

function CollapsedTooltip({ title, children }: Readonly<{ title: string; children: ReactElement }>) {
  return (
    <Tooltip arrow enterDelay={300} placement="right" title={title}>
      {children}
    </Tooltip>
  )
}

function MaybeCollapsedTooltip({
  isCollapsed,
  title,
  children,
}: Readonly<{ isCollapsed: boolean; title: string; children: ReactElement }>) {
  return isCollapsed ? <CollapsedTooltip title={title}>{children}</CollapsedTooltip> : (children as ReactNode)
}

function ListItemLink({ icon, activePath, isCollapsed = false, primary, to, onClick }: Readonly<ListItemLinkProps>) {
  const theme = useTheme()
  const envTheme = environment.theme

  const { pathname } = useLocation()
  const matchResult = typeof activePath === 'string' ? useMatch(activePath) : null
  const isActive = typeof activePath === 'function' ? activePath(pathname) : matchResult !== null

  const primary500 = theme.palette.primary.main
  const activeBg = alpha(primary500, 0.1)
  const hoverBg = alpha(primary500, 0.045)
  const activeHoverBg = alpha(primary500, 0.14)

  const iconColor = isActive ? primary500 : alpha(theme.palette.text.secondary, 0.7)
  const itemBg = isActive ? activeBg : 'transparent'
  const itemHoverBg = isActive ? activeHoverBg : hoverBg

  return (
    <MaybeCollapsedTooltip isCollapsed={isCollapsed} title={primary}>
      <li style={{ margin: getMargin(envTheme, theme) }}>
        <ListItem
          component={Link}
          sx={{
            py: 0.5,
            pl: 2.5,
            pr: 2,
            backgroundColor: itemBg,
            transition: ITEM_COLOR_TRANSITION,
            '&:hover': {
              backgroundColor: itemHoverBg,
            },
            '&:hover .ListItemLink-icon': {
              color: primary500,
            },
          }}
          to={to}
          onClick={onClick}
        >
          {icon && (
            <ListItemIcon
              className="ListItemLink-icon"
              sx={{
                color: iconColor,
                minWidth: 32,
                transition: ITEM_COLOR_TRANSITION,
                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
              }}
            >
              {icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={primary}
            primaryTypographyProps={{
              sx: {
                fontSize: '0.8125rem',
                fontWeight: isActive ? 600 : 400,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'clip',
                opacity: isCollapsed ? 0 : 1,
                transform: isCollapsed ? 'translateX(-4px)' : 'translateX(0)',
                transition: labelTransition(isCollapsed),
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                },
              },
            }}
          />
        </ListItem>
      </li>
    </MaybeCollapsedTooltip>
  )
}

export default ListItemLink
